import React, { useMemo } from 'react'
import { useApp } from '../context/AppContext'
import { UI, inputClass } from '../utils/styles'
import { formatReadingValue } from '../utils/constants'
import { Plus, Search, Edit } from '../components/ui/Icons'
import { ButtonSecondary } from '../components/ui/Button'
import { Sliders } from 'lucide-react'

export function MetersPage() {
  const {
    meters,
    operation,
    isOwner,
    meterSearch,
    setMeterSearch,
    showMeterSearch,
    setShowMeterSearch,
    selectedMetric,
    setSelectedMetric,
    sortBy,
    setSortBy,
    showFilterControls,
    setShowFilterControls,
    filterUnits,
    setFilterUnits,
    expandedMeterId,
    setExpandedMeterId,
    setShowAddMeter,
    setShowMeterDetails,
    setMeterDetailsId,
    setEditingMeterId,
    setEditingMeter,
    getSeasonTotal,
    getLastIrrigation,
    getEntriesThisSeason,
    getLastReadingDate,
    getMeterReadings,
  } = useApp()

  const getMetricValue = (meter) => {
    if (selectedMetric === 'seasonTotal') return getSeasonTotal(meter.id)
    if (selectedMetric === 'lastIrrigation') return getLastIrrigation(meter.id)
    if (selectedMetric === 'meterType') return meter.units || 'Not set'
    if (selectedMetric === 'lastReadingDate') return getLastReadingDate(meter.id)
    return getEntriesThisSeason(meter.id)
  }

  const unitTypes = useMemo(
    () => [...new Set(meters.map(m => m.units).filter(Boolean))].sort(),
    [meters]
  )

  // Memoized so typing elsewhere in the app doesn't re-filter and re-sort
  // the whole meter list on every keystroke.
  const sortedMeters = useMemo(() => {
    const filtered = meters.filter(m =>
      m.name.toLowerCase().includes(meterSearch.toLowerCase()) &&
      (!filterUnits || m.units === filterUnits)
    )
    return filtered.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name)
      if (selectedMetric === 'meterType') return (a.units || '').localeCompare(b.units || '')
      if (selectedMetric === 'lastReadingDate') {
        const aDate = getLastReadingDate(a.id)
        const bDate = getLastReadingDate(b.id)
        return new Date(bDate || 0) - new Date(aDate || 0)
      }
      const aVal = parseFloat(getMetricValue(a)) || 0
      const bVal = parseFloat(getMetricValue(b)) || 0
      return bVal - aVal
    })
  }, [meters, meterSearch, filterUnits, sortBy, selectedMetric,
      getSeasonTotal, getLastIrrigation, getEntriesThisSeason, getLastReadingDate])


  return (
    <div className="px-4 md:px-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 py-4">
        <div className="flex items-center justify-between relative">
          <h2 className="text-lg font-semibold text-slate-800 tracking-tight">Meters ({meters.length})</h2>
          {operation?.op_name && (
            <span className="text-sm text-slate-400 absolute left-1/2 -translate-x-1/2">{operation.op_name}</span>
          )}
          <div className="flex items-center gap-1">
            <button onClick={() => setShowMeterSearch(!showMeterSearch)} className={`p-2 text-slate-500 hover:text-slate-700 ${showMeterSearch ? 'bg-slate-100 rounded-lg' : ''}`}>
              <Search />
            </button>
            <button onClick={() => setShowFilterControls(!showFilterControls)} className={`p-2 text-slate-500 hover:text-slate-700 ${showFilterControls ? 'bg-slate-100 rounded-lg' : ''}`}>
              <Sliders size={20} />
            </button>
          </div>
        </div>

        {showMeterSearch && (
          <div className="relative">
            <input type="text" value={meterSearch} onChange={(e) => setMeterSearch(e.target.value)} placeholder="Search meters..." className={inputClass} autoFocus />
            {meterSearch && (
              <button onClick={() => setMeterSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
        )}

        {showFilterControls && (
          <div className={`${UI.glass} ${UI.border} ${UI.radius} p-4 space-y-3`}>
            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1.5">View</label>
              <select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)} className={inputClass}>
                <option value="seasonTotal">Season Total</option>
                <option value="lastIrrigation">Last Irrigation</option>
                <option value="entries">Entries This Season</option>
                <option value="lastReadingDate">Last Reading Date</option>
                <option value="meterType">Meter Type</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1.5">Sort by</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputClass}>
                <option value="name">Name</option>
                <option value="value">Value</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Meter List */}
      <div>
        {/* Add Meter Row */}
        {isOwner && (
          <>
            <div
              className="flex items-center py-3.5 pl-4 pr-4 cursor-pointer active:bg-slate-50"
              onClick={() => setShowAddMeter(true)}
            >
              <div className="flex items-center gap-2 text-sky-700">
                <Plus />
                <span className="font-medium">Add Meter</span>
              </div>
            </div>
            <div className="border-b border-slate-200 ml-4" />
          </>
        )}

        {sortedMeters.length > 0 ? (
          sortedMeters.map((meter, idx) => {
            const displayValue = getMetricValue(meter)
            const displayUnit = (selectedMetric === 'entries' || selectedMetric === 'meterType' || selectedMetric === 'lastReadingDate') ? '' : '"'
            const hasSeasonStart = meter.season_starting_reading !== null && meter.season_starting_reading !== undefined
            const isExpanded = expandedMeterId === meter.id

            return (
              <div key={meter.id}>
                {/* Row */}
                <div
                  className="flex items-center justify-between py-3.5 pl-4 pr-4 cursor-pointer active:bg-slate-50"
                  onClick={() => setExpandedMeterId(isExpanded ? null : meter.id)}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-base truncate">{meter.name}</h3>
                      {isOwner && isExpanded && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingMeterId(meter.id); setEditingMeter({...meter}) }}
                          className="p-1 text-slate-400 hover:text-sky-700 flex-shrink-0"
                          title="Edit meter"
                        >
                          <Edit />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{meter.acres} acres</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    {hasSeasonStart ? (
                      selectedMetric === 'meterType' ? (
                        <p className="text-sm font-medium text-slate-500">{displayValue}</p>
                      ) : selectedMetric === 'lastReadingDate' ? (
                        displayValue ? (
                          <div>
                            <p className="text-sm font-medium text-slate-900">{new Date(displayValue).toLocaleDateString()}</p>
                            <p className="text-xs text-slate-400">{formatReadingValue([...getMeterReadings(meter.id)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.end, meter.units)}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400">No readings</p>
                        )
                      ) : (
                        <p className="text-2xl font-bold text-slate-900">{displayValue}{displayUnit}</p>
                      )
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setMeterDetailsId(meter.id); setShowMeterDetails(true) }}
                        className="text-sm font-medium text-sky-700 hover:text-sky-800"
                      >
                        Set up &rarr;
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="pl-7 pr-4 pb-4 space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div className="bg-slate-50 rounded-xl p-2">
                        <p className="text-slate-500 text-xs">Season</p>
                        <p className="font-semibold">{getSeasonTotal(meter.id)}"</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-2">
                        <p className="text-slate-500 text-xs">Last 2 Readings</p>
                        {(() => {
                          const sorted = [...getMeterReadings(meter.id)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          const recent = sorted.filter(r => !(parseFloat(r.start) === parseFloat(r.end) && parseFloat(r.inches) === 0)).slice(0, 2)
                          return recent.length > 0 ? (
                            <div className="space-y-0.5">
                              {recent.map(r => (
                                <p key={r.id} className="font-semibold">{parseFloat(r.inches).toFixed(2)}"</p>
                              ))}
                            </div>
                          ) : <p className="font-semibold text-slate-400">—</p>
                        })()}
                      </div>
                      <div className="bg-slate-50 rounded-xl p-2">
                        <p className="text-slate-500 text-xs">Entries</p>
                        <p className="font-semibold">{getEntriesThisSeason(meter.id)}</p>
                      </div>
                    </div>

                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Units:</span>
                        <span className="text-slate-700">{meter.units || 'Not set'}</span>
                      </div>
                      {meter.brand && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Brand:</span>
                          <span className="text-slate-700">{meter.brand}</span>
                        </div>
                      )}
                      {meter.well_output_gpm && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Well Output:</span>
                          <span className="text-slate-700">{meter.well_output_gpm} GPM</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-500">Season Start:</span>
                        <span className={`${hasSeasonStart ? 'text-slate-700' : 'text-slate-400'}`}>
                          {hasSeasonStart ? (
                            <>
                              {formatReadingValue(meter.season_starting_reading, meter.units)}
                              {meter.season_starting_date && (
                                <span className="text-slate-400 ml-1">({new Date(meter.season_starting_date).toLocaleDateString()})</span>
                              )}
                            </>
                          ) : 'Not set'}
                        </span>
                      </div>
                      {meter.notes && (
                        <div className="pt-1">
                          <span className="text-slate-500">Notes: </span>
                          <span className="text-slate-600 italic">{meter.notes}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <ButtonSecondary
                        onClick={() => { setMeterDetailsId(meter.id); setShowMeterDetails(true) }}
                        className="flex-1 text-sm !py-2"
                      >
                        Reading History
                      </ButtonSecondary>
                    </div>
                  </div>
                )}

                {/* Divider */}
                {idx < sortedMeters.length - 1 && <div className="border-b border-slate-200 ml-4" />}
              </div>
            )
          })
        ) : (
          <div className="py-16 text-center">
            {meterSearch ? (
              <p className="text-slate-500">No meters matching "{meterSearch}"</p>
            ) : (
              <>
                <p className="text-slate-400 text-sm mb-4">No meters added yet</p>
                {isOwner && (
                  <button
                    onClick={() => setShowAddMeter(true)}
                    className="text-sm font-medium text-sky-700 hover:text-sky-800 transition-colors"
                  >
                    Add your first meter
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
